import { useEffect, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactTimeAgo from 'react-time-ago';
import { createColumnHelper } from '@tanstack/react-table';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import { EllipsisVertical } from 'lucide-react';

import { ActivityLog } from '@/models/ActivityLogsByWorkspace';

// Updated interface based on your data structure

export const useActivityLogsTable = () => {
  TimeAgo.setDefaultLocale(en.locale);
  TimeAgo.addLocale(en);

  const { t } = useTranslation();
  // Type assertion needed due to TypeScript's type instantiation depth limit
  const columnHelper = createColumnHelper<ActivityLog>() as any;

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRefs: { [rowId: string]: React.RefObject<HTMLDivElement> } = {};

  const handleDropdownClick = (event: React.MouseEvent, rowId: string) => {
    event.stopPropagation();

    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(rowId);
    }
  };

  useEffect(() => {
    const dropdownRefKeys = Object.keys(dropdownRefs);

    const closeDropdownsExceptCurrent = (currentRowId: string | null) => {
      dropdownRefKeys.forEach((rowId) => {
        if (rowId === currentRowId) {
          setOpenDropdownId(null);
        }
      });
    };

    const handleOutsideClick = () => {
      closeDropdownsExceptCurrent(openDropdownId);
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [openDropdownId]);

  const columns = [
    // Event Type column
    columnHelper.accessor('eventType', {
      header: () => <div>Event Type</div>,
      cell: (info: any) => {
        const eventType = info.renderValue();
        // Format event type (replace underscores with spaces and capitalize)
        const formattedName = eventType
          ? eventType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          : '-';
        return <span>{formattedName}</span>;
      },
    }),
    columnHelper.accessor('userEmail', {
      header: () => <div>User Email</div>,
      cell: (info: any) => <span>{info.renderValue() || '-'}</span>,
    }),
    // Timestamp column
    columnHelper.accessor('timestamp', {
      header: () => <div>Timestamp</div>,
      cell: (info: any) => {
        const timestamp = info.renderValue();
        if (typeof timestamp === 'number' && !isNaN(timestamp)) {
          const date = new Date(timestamp);
          return (
            <div className="flex flex-col">
              <span>{date.toLocaleString()}</span>
              <span className="text-xs text-custom-darkSilver">
                <ReactTimeAgo date={date} locale="en-US" />
              </span>
            </div>
          );
        }
        return <span className="text-gray-500">-</span>;
      },
    }),
    // Data column
    columnHelper.accessor('data', {
      header: () => <div>Data</div>,
      cell: (info: any) => {
        const data = info.renderValue();
        if (data && Object.keys(data).length > 0) {
          return (
            <div className="max-w-xs max-h-40 overflow-auto">
              <pre className="text-xs bg-gray-50 p-2 rounded">{JSON.stringify(data, null, 2)}</pre>
            </div>
          );
        }
        return <span className="text-gray-500">-</span>;
      },
    }),
  ];

  return columns;
};
